'use client';

import { cn, Div, Image, Span } from '@scatterlab/zeta-core';
import {
  ComponentPropsWithoutRef,
  ComponentRef,
  createElement,
  ElementType,
  memo,
  ReactNode,
  RefAttributes,
} from 'react';
import { type ImageSourcePropType } from 'react-native';

import { DEFAULT_CHARACTER_PROFILE_IMAGE_URL } from '../constants';
import { DashIcon, TriangleDownIcon, TriangleUpIcon } from '../icons';
import RankingNum from './RankingNum';
import CreatorTag, { CreatorTagProps } from '../tag/CreatorTag';
import Hashtags from '../tag/Hashtags';
import SymbolTag from '../tag/SymbolTag';

export type RankingListItemCardProps<
  ImageContainerComponent extends ElementType = typeof Div,
  HeadComponent extends ElementType = typeof Div,
  CreatorTagComponent extends ElementType = typeof Span,
  HashtagComponent extends ElementType = typeof Span,
> = ComponentPropsWithoutRef<typeof Div> &
  RefAttributes<ComponentRef<typeof Div>> & {
    imageContainerComponent?: ImageContainerComponent;
    imageContainerProps?: ComponentPropsWithoutRef<ImageContainerComponent>;
    headComponent?: HeadComponent;
    headProps?: ComponentPropsWithoutRef<HeadComponent>;

    image?: string | ImageSourcePropType;
    imageUnoptimized?: boolean;
    creatorUsername: string;
    creatorTagComponent?: CreatorTagComponent;
    creatorTagProps?: Omit<
      CreatorTagProps<CreatorTagComponent>,
      'component' | 'label'
    >;
    name: string;
    description: string;
    tagLabel?: string;
    hashtags?: string[];
    hashtagComponent?: HashtagComponent;
    hashtagProps?: (
      hashtag: string,
    ) => ComponentPropsWithoutRef<HashtagComponent>;
    isHoverEffect?: boolean;
    addon?: ReactNode;
    verified?: boolean;
    facialSupported?: boolean;
    ttsSupported?: boolean;
    rank: number;
    rankDiff: number;
    isNew: boolean;
    interactionCountWithRegen?: number;
    bucket?: string;
  };

const RankingListItemCard = <
  ImageContainerComponent extends ElementType = typeof Div,
  HeadComponent extends ElementType = typeof Div,
  CreatorTagComponent extends ElementType = typeof Span,
  HashtagComponent extends ElementType = typeof Span,
>({
  imageContainerComponent,
  imageContainerProps,
  headComponent,
  headProps,

  image,
  imageUnoptimized,
  creatorUsername,
  creatorTagComponent,
  creatorTagProps,
  name,
  description,
  tagLabel,
  hashtags,
  hashtagComponent,
  hashtagProps,
  className,
  isHoverEffect,
  addon,
  verified,
  facialSupported,
  ttsSupported,
  rank,
  rankDiff,
  isNew,
  interactionCountWithRegen,
  ...props
}: RankingListItemCardProps<
  ImageContainerComponent,
  HeadComponent,
  CreatorTagComponent,
  HashtagComponent
>) => (
  <Div
    {...props}
    className={cn('group/item flex w-full flex-row gap-3 py-3', className)}
  >
    {createElement(
      imageContainerComponent ?? Div,
      {
        ...imageContainerProps,
        className: cn(
          'relative flex flex-row items-end self-center',
          imageContainerProps?.className,
        ),
      },
      <>
        <RankingNum
          rank={rank}
          className="absolute bottom-0 left-0 h-[50px] w-[34px] text-gray-600"
        />
        <Div className="relative ml-5 aspect-square size-[100px] rounded-lg bg-gray-main object-cover shadow-leftUp">
          <Image
            src={image ?? DEFAULT_CHARACTER_PROFILE_IMAGE_URL}
            alt={`${creatorUsername}의 ${name}`}
            fill
            sizes="100px"
            className="size-full rounded-lg object-cover"
            unoptimized={imageUnoptimized}
          />
          <Div
            className={cn(
              'absolute inset-0 z-[1] w-full rounded-lg transition-colors',
              isHoverEffect ? 'group-hover/item:bg-black/40' : '',
            )}
          />
          {tagLabel && (
            <SymbolTag label={tagLabel} className="absolute left-2.5 top-2.5" />
          )}
        </Div>
      </>,
    )}
    <Div className="flex w-full shrink flex-col gap-1.5 self-center">
      {createElement(
        headComponent ?? Div,
        {
          ...headProps,
          className: cn('flex flex-col gap-1', headProps?.className),
        },
        <>
          <Div className="flex flex-row items-center justify-between gap-2">
            <Div className="flex shrink flex-row items-center gap-1">
              <Span
                className="body14 line-clamp-1 shrink font-medium text-gray-50"
                title={name}
              >
                {name}
              </Span>
              {isNew && <Span className="body12 text-success-light">new</Span>}
              {!isNew && rankDiff > 0 && (
                <>
                  <TriangleUpIcon className="h-[9px] w-[7.8px]" />
                  <Span className="body12 -ml-0.5 text-success-light">
                    {rankDiff >= 100 ? '99+' : rankDiff}
                  </Span>
                </>
              )}
              {!isNew && rankDiff === 0 && (
                <DashIcon className="h-0.5 w-1.5 text-white/50" />
              )}
              {!isNew && rankDiff < 0 && (
                <>
                  <TriangleDownIcon className="h-[9px] w-[7.8px] text-white/50" />
                  <Span className="body12 -ml-0.5 text-white/50">
                    {rankDiff <= -100 ? '99+' : -rankDiff}
                  </Span>
                </>
              )}
            </Div>
            {addon}
          </Div>
          <Span
            className="body14 mr-6 line-clamp-2 text-white/50"
            title={description}
          >
            {description}
          </Span>
        </>,
      )}
      {hashtags && hashtags.length !== 0 && (
        <Hashtags
          hashtags={hashtags}
          hashtagProps={hashtagProps}
          hashtagComponent={hashtagComponent}
        />
      )}
      {createElement(CreatorTag, {
        className: 'self-start',
        ...creatorTagProps,
        component: creatorTagComponent,
        label: creatorUsername,
        verified,
      })}
    </Div>
  </Div>
);

export default memo(RankingListItemCard) as typeof RankingListItemCard;
